import React, { useMemo, useState, useEffect } from 'react'
import { polygon } from '@turf/turf'
import { useHistory } from 'react-router-dom'
import { Grid, MenuItem, Select as Multiselect } from '@material-ui/core'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import { Col, Grid as BootstrapGrid, Row } from 'react-bootstrap'
import Typography from '@material-ui/core/Typography'
import Slider from '@material-ui/core/Slider'
import DisplayMap from '../GMap/DisplayMap'
import BarChart from '../../Containers/StatsOverview/components/CampaignPerforming/Charts/BarChart'
import {
  COLORS_SCALE,
  dayMarks,
  dayOfWeekMissionsMapInitial,
  timeMarks,
  timeOfDayMissionsMapInitial,
} from './const'
import {
  browserSafeNewDate,
  dropTimeZoneFromDate,
  getLongWeekDay,
} from '../../shared/libraries/utils/date'
import { calculateTimeOfDay } from '../../Utils/Mission'

import SubmitButton from '../Form/SubmitButton'
import { authorization } from '../../Module/Auth/Auth'
import { generateFootFallData } from '../../Containers/Campaigns/gql'
import { getFormatedArea } from '../../Containers/BuilderMap/util'
import { debounce } from '../../Utils/Functions'
import { isPointInside } from './utils'
import GenerateButton from '../../Containers/Campaigns/RosterView/components/GenerateButton/GenerateButton'
import { PATHS } from '../../Config/Routing/paths'

const MissionsOverview = ({
  rosters,
  city,
  isShowMissionGenerateButton = false,
}) => {
  const history = useHistory()

  const availableWeeks = rosters.map(({ weekNum }) => weekNum)
  const [selectedWeeks, setSelectedWeeks] = useState(availableWeeks)
  const [selectedFilters, setSelectedFilters] = useState({
    day: 1,
    time: [8, 12],
  })
  const [heatMapType, setHeatMapType] = useState('missions')
  const [footFallHeatData, setFootFallHeatData] = useState([])
  const [filteredFootFallHeatData, setFilteredFootFallHeatData] = useState([])
  const [mapView, setMapView] = useState(null)

  const [isLoading, setIsLoading] = useState(false)

  const onBoundsChanged = async ({ map }) => {
    try {
      const latLngBound = map.getBounds()

      if (!latLngBound) return
      const formattedArea = await getFormatedArea(latLngBound)
      setMapView(formattedArea.coordinates)
    } catch (error) {
      console.error('PathFinder error.')
      throw error
    }
  }

  const addListener = ({ map }) => {
    map.addListener(
      'bounds_changed',
      debounce(() => {
        onBoundsChanged({ map })
      }, 300),
    )
  }

  useEffect(() => {
    if (mapView && footFallHeatData.length && !isLoading) {
      const turfPolygon = polygon(mapView)
      const filteredPoints = footFallHeatData.filter(
        ({ longitude, latitude }) => {
          return isPointInside({ turfPolygon, longitude, latitude })
        },
      )
      setFilteredFootFallHeatData(filteredPoints)
    }
  }, [mapView && mapView[0][0][0], footFallHeatData.length, isLoading])

  const getFootFallData = async () => {
    setIsLoading(true)
    const token = authorization.getAPIKey()
    const params = {
      location_city_id: city.id,
      hour_start: selectedFilters.time[0],
      hour_end: selectedFilters.time[1],
      weekday_start: selectedFilters.day,
    }

    const encodedParams = Object.entries(params)
      .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
      .join('&')

    const data = await generateFootFallData({ token, encodedParams })

    const footfallHeatMapData = data.reduce((acc, item) => {
      acc.push({
        latitude: item.latitude,
        longitude: item.longitude,
        weight: item.foot_traffic,
      })
      return acc
    }, [])
    setFootFallHeatData(footfallHeatMapData)
    setFilteredFootFallHeatData(footfallHeatMapData)
    setIsLoading(false)
  }

  const getHeatMapDataFromMissions = data => {
    const groupedMissions = Object.values(data)
    const heatMapData = groupedMissions.reduce((acc, group) => {
      acc.push({
        latitude: group.location.latitude,
        longitude: group.location.longitude,
        weight: group.missions.length,
      })
      return acc
    }, [])
    return heatMapData
  }

  const { selectedMissions, dayOfWeekData, timeOfDayData } = useMemo(() => {
    const selectedMissions = rosters.reduce((acc, roster) => {
      if (selectedWeeks && selectedWeeks.length) {
        if (selectedWeeks.includes(roster.weekNum)) {
          acc = [...acc, ...(roster.missions || [])]
        }
      }
      return acc
    }, [])

    const { dayOfWeekMissions, timeOfDayMissions } = selectedMissions.reduce(
      (acc, mission) => {
        const timeOfDay = calculateTimeOfDay(
          mission.dateTime,
          city.lunchCheckInTime,
          city.afternoonCheckInTime,
        )
        const dayOfWeek = getLongWeekDay(
          browserSafeNewDate(dropTimeZoneFromDate(mission.dateTime)).getDay(),
        )

        acc.timeOfDayMissions[timeOfDay] = acc.timeOfDayMissions[timeOfDay] + 1
        acc.dayOfWeekMissions[dayOfWeek] = acc.dayOfWeekMissions[dayOfWeek] + 1
        return acc
      },
      {
        timeOfDayMissions: { ...timeOfDayMissionsMapInitial },
        dayOfWeekMissions: { ...dayOfWeekMissionsMapInitial },
      },
    )
    return {
      selectedMissions,
      dayOfWeekData: {
        categories: Object.keys(dayOfWeekMissions),
        series: [
          {
            name: 'Missions',
            data: Object.values(dayOfWeekMissions),
          },
        ],
      },
      timeOfDayData: {
        categories: Object.keys(timeOfDayMissions),
        series: [
          {
            name: 'Missions',
            data: Object.values(timeOfDayMissions),
          },
        ],
      },
    }
  }, [rosters, selectedWeeks])

  // Letterbox missions are drawn from a boundary and have no location at all,
  // so anything keyed by `location.id` has to skip them — dereferencing it
  // blanked the whole City Plan page for a letterbox city plan.
  const mappableMissions = useMemo(
    () => selectedMissions.filter(mission => mission.location),
    [selectedMissions],
  )

  const missionsByLocationIdMap = useMemo(
    () =>
      mappableMissions.reduce((acc, mission) => {
        const locationId = mission.location.id
        if (!acc[locationId]) {
          acc[locationId] = {
            missions: [],
            flyersDistributed: 0,
          }
        }
        acc[locationId].missions.push(mission)
        acc[locationId].flyersDistributed += mission.totalFlyersDistributed
        acc[locationId].location = mission.location
        return acc
      }, {}),
    [mappableMissions],
  )

  const markers = useMemo(() => {
    const selectedMissionsByLocationIdMap = mappableMissions.reduce(
      (acc, mission) => {
        const locationId = mission.location.id
        if (!acc[locationId]) {
          acc[locationId] = { missions: [], flyersDistributed: 0 }
        }
        acc[locationId].missions.push(mission)
        acc[locationId].flyersDistributed += mission.totalFlyersDistributed
        return acc
      },
      {},
    )
    return mappableMissions.map(mission => ({
      position: {
        lat: parseFloat(mission.location.latitude),
        lng: parseFloat(mission.location.longitude),
      },
      options: {
        infoWindowSettings: {
          content: `
              <div>
                <h4>${mission.location.name} </h4>
                <br />
                <p style={font-weight: bold;}>Stats for selected period</p>
                <p>
                  Missions: ${
                    selectedMissionsByLocationIdMap[mission.location.id]
                      .missions.length
                  }
                </p>
                <p>Flyers distributed:${
                  selectedMissionsByLocationIdMap[mission.location.id]
                    .flyersDistributed
                }</p>
                <p>Period: Weeks ${selectedWeeks.join(', ')}</p>
                <br />
                <p style={font-weight: bold;}>Total stats for this location</p>
                <p>Flyers distributed:${
                  missionsByLocationIdMap[mission.location.id].flyersDistributed
                }</p>
                <p>
                  Missions: ${
                    missionsByLocationIdMap[mission.location.id].missions.length
                  }
                </p>
              </div>
            `,
        },
        icon: {
          fillColor: 'green',
          strokeColor: 'green',
          strokeWeight: 3,
          scale: 3,
        },
      },
    }))
  }, [missionsByLocationIdMap, mappableMissions])
  const onMapLoad = (google, { map, markers }) => {
    if (map) {
      const bounds = new google.maps.LatLngBounds()
      addListener({ map })

      if (markers && markers.length > 0) {
        markers.forEach(({ position }) => bounds.extend(position))
        map.fitBounds(bounds)
      } else {
        map.setCenter({
          lat: city.latitude,
          lng: city.longitude,
        })
      }
    }
  }

  return (
    <div>
      <br />
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <ButtonGroup
            style={{ margin: '14px 0 14px 0' }}
            disableElevation
            color="primary"
          >
            <SubmitButton
              value="Missions"
              variant={heatMapType === 'missions' ? 'contained' : 'outlined'}
              onClick={() => {
                setFootFallHeatData([])
                setFilteredFootFallHeatData([])
                setHeatMapType('missions')
              }}
            />
            <SubmitButton
              value="Footfall traffic"
              variant={heatMapType == 'footfall' ? 'contained' : 'outlined'}
              onClick={() => setHeatMapType('footfall')}
            />
          </ButtonGroup>
          {isShowMissionGenerateButton ? (
            <GenerateButton
              onClick={() => {
                history.push(
                  `${history.location.pathname}${PATHS.cityPlan.aiRoster}`,
                )
              }}
              hidden={false}
            />
          ) : null}
        </div>
        {heatMapType == 'footfall' && (
          <BootstrapGrid fluid>
            <Row style={{ padding: '14px 0 14px 0', alignItems: 'center' }}>
              <Col xs={1}>
                <Typography>Day:</Typography>
              </Col>
              <Col xs={4}>
                <Slider
                  name={'Day'}
                  defaultValue={0}
                  aria-labelledby="continuous-slider"
                  step={1}
                  onChange={(event, value) =>
                    setSelectedFilters(prev => ({ ...prev, day: value }))
                  }
                  min={0}
                  max={6}
                  marks={dayMarks}
                  track={false}
                  valueLabelDisplay={'off'}
                />
              </Col>
            </Row>
            <Row style={{ padding: '14px 0 14px 0', alignItems: 'center' }}>
              <Col xs={1}>
                <Typography>Time range:</Typography>
              </Col>
              <Col xs={9}>
                <Slider
                  name={'Time'}
                  value={selectedFilters.time}
                  onChange={(event, newValue) => {
                    setSelectedFilters(prev => ({ ...prev, time: newValue }))
                  }}
                  step={1}
                  min={1}
                  max={24}
                  marks={timeMarks}
                  valueLabelDisplay={'off'}
                  aria-labelledby="range-slider"
                  getAriaValueText={value => value}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={2}>
                <SubmitButton
                  value="Generate heatmap"
                  variant={'contained'}
                  disabled={isLoading}
                  onClick={async () => await getFootFallData()}
                  style={{ margin: '14px 0 14px 0' }}
                />
              </Col>
              <Col xs={1}>
                {isLoading && <Typography>Loading...</Typography>}
              </Col>
            </Row>
          </BootstrapGrid>
        )}
        <DisplayMap
          key={`map+${heatMapType}`}
          onMapLoad={onMapLoad}
          markers={markers}
          heatMapData={
            heatMapType == 'footfall'
              ? filteredFootFallHeatData
              : getHeatMapDataFromMissions(missionsByLocationIdMap)
          }
        />
        <div
          style={{
            height: '64px',
            borderRadius: '0 0 16px 16px',
            borderLeft: '2px solid #E2E2FC',
            borderRight: '2px solid #E2E2FC',
            borderBottom: '2px solid #E2E2FC',
            padding: '20px 267px',
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          {availableWeeks.length > 1 ? (
            <Multiselect
              multiple
              disableUnderline
              value={selectedWeeks}
              onChange={(e, i, v) => {
                let data = e.target.value

                if (i.props.value.includes('All')) {
                  data = availableWeeks
                  if (selectedWeeks.length === availableWeeks.length) {
                    data = []
                  }
                }
                if (i.props.value.includes('None')) {
                  data = []
                }
                setSelectedWeeks(data)
              }}
              style={{
                width: '150px',
                border: '1px solid #E2E2FC',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 150,
                  },
                },
                getContentAnchorEl: () => null,
              }}
            >
              <MenuItem
                key={'All'}
                value={
                  selectedWeeks.length === availableWeeks.length
                    ? 'None'
                    : 'All'
                }
                style={{
                  backgroundColor:
                    selectedWeeks.length === availableWeeks.length
                      ? '#E2E2FC'
                      : 'transparent',
                }}
              >
                {selectedWeeks.length === availableWeeks.length
                  ? 'None'
                  : 'All'}
              </MenuItem>
              {availableWeeks.map(week => (
                <MenuItem
                  key={week}
                  value={week}
                  style={{
                    backgroundColor: selectedWeeks.includes(week)
                      ? '#E2E2FC'
                      : 'transparent',
                  }}
                >
                  {week}
                </MenuItem>
              ))}
            </Multiselect>
          ) : null}
          {COLORS_SCALE.map(item => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  marginRight: '12px',
                  height: '24px',
                  width: '24px',
                  borderRadius: '4px',
                  background: item.color,
                }}
              />{' '}
              <div style={{ fontSize: '16px' }}>{item.label}</div>
            </div>
          ))}
        </div>

        <br />
        <Grid container>
          <Grid item xs={6}>
            <BarChart
              key={`chart-dayOfWeekData-${rosters.length}`}
              title="Distribution per week day"
              yAxisTitle="# of Missions"
              series={dayOfWeekData.series}
              tooltipValueSuffix=" at this day"
              categories={dayOfWeekData.categories}
            />
          </Grid>
          <Grid item xs={6}>
            <BarChart
              key={`chart-timeOfDayData-${rosters.length}`}
              title="Distribution per time of the day"
              yAxisTitle="# of Missions"
              series={timeOfDayData.series}
              tooltipValueSuffix=" at this time"
              categories={timeOfDayData.categories}
            />
          </Grid>
        </Grid>
      </div>
      <br />
    </div>
  )
}

export default MissionsOverview
